import { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppContext, useAsyncData } from '@/hooks';
import { api } from '@/api/client';
import { formatRelative } from '@/utils/format';
import type { AcademicYear } from '@/types';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { FormField } from '@/components/ui/FormField';
import { useToast } from '@/components/ui/Toast';
import { Icon } from '@/components/icons';
import styles from './AdminAcademicYearsPage.module.css';

interface AcademicYearForm {
  label: string;
  startDate: string;
  endDate: string;
}

const EMPTY_FORM: AcademicYearForm = { label: '', startDate: '', endDate: '' };

const STATUS_CLASS: Record<string, string> = {
  DRAFT: 'statusDraft',
  SUBMITTED: 'statusSubmitted',
  APPROVED: 'statusApproved',
  ACTIVE: 'statusActive',
  CLOSED: 'statusClosed',
};

const STATUS_KEY: Record<string, string> = {
  DRAFT: 'admin.academicYear.draft',
  SUBMITTED: 'admin.academicYear.submitted',
  APPROVED: 'admin.academicYear.approved',
  ACTIVE: 'admin.academicYear.active',
  CLOSED: 'admin.academicYear.closed',
};

/** One year minus a day: Sep 1 2026 → Aug 31 2027, the near-universal shape. */
function suggestedEndDate(start: string): string {
  const date = new Date(`${start}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  date.setFullYear(date.getFullYear() + 1);
  date.setDate(date.getDate() - 1);
  return date.toISOString().slice(0, 10);
}

/** "2026/2027" when the year spans a calendar boundary, "2026" when it doesn't. */
function suggestedLabel(start: string, end: string): string {
  const startYear = start.slice(0, 4);
  const endYear = end.slice(0, 4);
  if (!startYear) return '';
  return endYear && endYear !== startYear ? `${startYear}/${endYear}` : startYear;
}

function monthsBetween(start: string, end: string): number {
  const a = new Date(`${start}T00:00:00`).getTime();
  const b = new Date(`${end}T00:00:00`).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b <= a) return 0;
  return Math.max(1, Math.round((b - a) / (30.44 * 86_400_000)));
}

function overlaps(aStart: string, aEnd: string, bStart: string, bEnd: string): boolean {
  return aStart <= bEnd.slice(0, 10) && bStart.slice(0, 10) <= aEnd;
}

export function AdminAcademicYearsPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';

  const { state, reload, replace } = useAsyncData<AcademicYear[]>(
    `admin-academic-years-${institutionId}`,
    () => api.admin.academicYears.list(institutionId),
    !!institutionId,
  );

  const [newModalOpen, setNewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; year: AcademicYear | null; action: 'delete' | 'submit' }>({ open: false, year: null, action: 'delete' });
  const [form, setForm] = useState<AcademicYearForm>(EMPTY_FORM);
  // Which fields the person has typed into by hand. Auto-fill only writes over
  // fields it owns; the moment somebody edits the label or end date themselves,
  // suggestions stop touching that field.
  const [touched, setTouched] = useState({ label: false, end: false });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const years = state.status === 'ready' ? state.data : [];

  // Newest first: the year being worked on is nearly always the latest one.
  const sortedYears = useMemo(
    () => [...years].sort((a, b) => (b.startDate ?? '').localeCompare(a.startDate ?? '')),
    [years],
  );

  const today = new Date().toISOString().slice(0, 10);
  const currentYear = useMemo(
    () => sortedYears.find((y) => (y.startDate?.slice(0, 10) ?? '') <= today && today <= (y.endDate?.slice(0, 10) ?? '')),
    [sortedYears, today],
  );
  const upcomingYear = useMemo(
    () =>
      [...years]
        .filter((y) => (y.startDate?.slice(0, 10) ?? '') > today)
        .sort((a, b) => (a.startDate ?? '').localeCompare(b.startDate ?? ''))[0],
    [years, today],
  );

  const currentProgress = useMemo(() => {
    if (!currentYear) return 0;
    const start = new Date(`${currentYear.startDate.slice(0, 10)}T00:00:00`).getTime();
    const end = new Date(`${currentYear.endDate.slice(0, 10)}T00:00:00`).getTime();
    const now = Date.now();
    if (end <= start) return 0;
    return Math.min(100, Math.max(0, Math.round(((now - start) / (end - start)) * 100)));
  }, [currentYear]);

  // ── Form intelligence ──────────────────────────────────────────────────

  const handleStartChange = (value: string) => {
    const next = { ...form, startDate: value };
    // Picking the start date does most of the work: the end date lands a year
    // later and the label writes itself, both still editable.
    if (!touched.end || !form.endDate) next.endDate = suggestedEndDate(value);
    if (!touched.label || !form.label) next.label = suggestedLabel(value, next.endDate);
    setForm(next);
  };

  const handleEndChange = (value: string) => {
    setTouched((prev) => ({ ...prev, end: true }));
    const next = { ...form, endDate: value };
    if (!touched.label || !form.label) next.label = suggestedLabel(form.startDate, value);
    setForm(next);
  };

  const dateError =
    form.startDate && form.endDate && form.endDate <= form.startDate
      ? t('admin.academicYears.endBeforeStart')
      : '';

  const overlapping = useMemo(() => {
    if (!form.startDate || !form.endDate || dateError) return null;
    return (
      years.find(
        (y) =>
          y.id !== editingId &&
          y.status !== 'CLOSED' &&
          overlaps(form.startDate, form.endDate, y.startDate ?? '', y.endDate ?? ''),
      ) ?? null
    );
  }, [years, form.startDate, form.endDate, dateError, editingId]);

  const durationMonths = monthsBetween(form.startDate, form.endDate);
  const formValid = !!form.label && !!form.startDate && !!form.endDate && !dateError;

  const openNew = () => {
    setForm(EMPTY_FORM);
    setTouched({ label: false, end: false });
    setEditingId(null);
    setNewModalOpen(true);
  };

  const openEdit = (year: AcademicYear) => {
    setEditingId(year.id);
    setForm({ label: year.label, startDate: year.startDate?.slice(0, 10) ?? '', endDate: year.endDate?.slice(0, 10) ?? '' });
    // An existing year's fields are all deliberate; suggestions stay out.
    setTouched({ label: true, end: true });
    setEditModalOpen(true);
  };

  // ── Mutations ──────────────────────────────────────────────────────────

  const handleCreate = async () => {
    if (!institutionId || !formValid) return;
    setSubmitting(true);
    try {
      const created = await api.admin.academicYears.create(institutionId, form);
      if (years) replace([...years, created]);
      setNewModalOpen(false);
      setForm(EMPTY_FORM);
      toast.success(t('admin.academicYears.saved'));
    } catch {
      toast.error(t('common.actionFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!institutionId || !editingId || !formValid) return;
    setSubmitting(true);
    try {
      const updated = await api.admin.academicYears.update(institutionId, editingId, form);
      if (years) replace(years.map((y) => (y.id === editingId ? updated : y)));
      setEditModalOpen(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      toast.success(t('admin.academicYears.saved'));
    } catch {
      toast.error(t('common.actionFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = useCallback(async () => {
    if (!institutionId || !confirmModal.year) return;
    setSubmitting(true);
    try {
      await api.admin.academicYears.delete(institutionId, confirmModal.year.id);
      if (years) replace(years.filter((y) => y.id !== confirmModal.year!.id));
      setConfirmModal({ open: false, year: null, action: 'delete' });
      toast.success(t('admin.academicYears.deleted'));
    } catch {
      toast.error(t('common.actionFailed'));
    } finally {
      setSubmitting(false);
    }
  }, [institutionId, confirmModal, years, replace, t, toast]);

  const handleSubmitForApproval = useCallback(async () => {
    if (!institutionId || !confirmModal.year) return;
    setSubmitting(true);
    try {
      await api.admin.academicYears.submitForApproval(institutionId, confirmModal.year.id);
      if (years) replace(years.map((y) => (y.id === confirmModal.year!.id ? { ...y, status: 'SUBMITTED' as const } : y)));
      setConfirmModal({ open: false, year: null, action: 'delete' });
      toast.success(t('admin.academicYears.submitted'));
    } catch {
      toast.error(t('common.actionFailed'));
    } finally {
      setSubmitting(false);
    }
  }, [institutionId, confirmModal, years, replace, t, toast]);

  // ── Rendering helpers ──────────────────────────────────────────────────

  const formatDate = (d?: string) => {
    if (!d) return '—';
    try {
      return new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return d;
    }
  };

  const timingFor = (year: AcademicYear): string => {
    const start = year.startDate?.slice(0, 10) ?? '';
    const end = year.endDate?.slice(0, 10) ?? '';
    if (start > today) return t('admin.academicYears.startsWhen', { when: formatRelative(start, i18n.language) });
    if (end < today) return t('admin.academicYears.endedWhen', { when: formatRelative(end, i18n.language) });
    return t('admin.academicYears.endsWhen', { when: formatRelative(end, i18n.language) });
  };

  const yearForm = (idPrefix: string, onSave: () => void) => (
    <div
      className={styles.dialogForm}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
          e.preventDefault();
          if (formValid && !submitting) onSave();
        }
      }}
    >
      <FormField
        id={`${idPrefix}-start`}
        label={t('admin.academicYears.startDate')}
        required
        hint={t('admin.academicYears.startHint')}
      >
        <input id={`${idPrefix}-start`} type="date" className={styles.formField} value={form.startDate} onChange={(e) => handleStartChange(e.target.value)} />
      </FormField>
      <FormField id={`${idPrefix}-end`} label={t('admin.academicYears.endDate')} required error={dateError || undefined}>
        <input id={`${idPrefix}-end`} type="date" className={styles.formField} value={form.endDate} min={form.startDate || undefined} onChange={(e) => handleEndChange(e.target.value)} />
      </FormField>
      <FormField id={`${idPrefix}-label`} label={t('admin.academicYears.label')} required>
        <input
          id={`${idPrefix}-label`}
          className={styles.formField}
          placeholder={t('admin.academicYears.labelPlaceholder')}
          value={form.label}
          onChange={(e) => {
            setTouched((prev) => ({ ...prev, label: true }));
            setForm({ ...form, label: e.target.value });
          }}
        />
      </FormField>

      {formValid && durationMonths > 0 && (
        <p className={styles.formSummary}>
          {formatDate(form.startDate)} – {formatDate(form.endDate)} ·{' '}
          {t('admin.academicYears.durationMonths', { count: durationMonths })}
        </p>
      )}
      {overlapping && (
        <p className={styles.formWarning}>
          <Icon name="warning" size={14} />
          {t('admin.academicYears.overlapsWarning', { label: overlapping.label })}
        </p>
      )}

      <div className={styles.dialogActions}>
        <Button variant="secondary" onClick={() => { setNewModalOpen(false); setEditModalOpen(false); }}>{t('common.cancel')}</Button>
        <Button onClick={onSave} loading={submitting} disabled={!formValid || submitting}>{t('common.save')}</Button>
      </div>
    </div>
  );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.headerName}>{t('admin.academicYears.title')}</h1>
          <p className={styles.headerSubtitle}>{t('admin.academicYears.subtitle')}</p>
        </div>
        <Button onClick={openNew}>{t('admin.academicYears.newAcademicYear')}</Button>
      </header>

      {state.status === 'loading' && (
        <div className={styles.loading}>{t('admin.academicYears.loading')}</div>
      )}

      {state.status === 'error' && (
        <Card className={styles.emptyState}>
          <Icon name="error" size={48} />
          <h2 className={styles.emptyStateTitle}>{t('admin.academicYears.title')}</h2>
          <p className={styles.emptyStateBody}>{t('common.error')}</p>
          <button className={styles.retryButton} onClick={reload}>{t('common.retry')}</button>
        </Card>
      )}

      {state.status === 'ready' && years.length === 0 && (
        <Card className={styles.emptyState}>
          <Icon name="academic" size={48} />
          <h2 className={styles.emptyStateTitle}>{t('admin.academicYears.noAcademicYears')}</h2>
          <p className={styles.emptyStateBody}>{t('admin.academicYears.createFirst')}</p>
          <Button onClick={openNew}>{t('admin.academicYears.newAcademicYear')}</Button>
        </Card>
      )}

      {state.status === 'ready' && years.length > 0 && (
        <>
          {/* Where the institution stands in its calendar, at a glance. */}
          {currentYear ? (
            <Card className={styles.currentCard}>
              <div className={styles.currentHeader}>
                <div>
                  <p className={styles.currentEyebrow}>{t('admin.academicYears.currentYear')}</p>
                  <h2 className={styles.currentLabel}>{currentYear.label}</h2>
                  <p className={styles.currentPeriod}>
                    {formatDate(currentYear.startDate)} – {formatDate(currentYear.endDate)} ·{' '}
                    {t('admin.academicYears.endsWhen', { when: formatRelative(currentYear.endDate, i18n.language) })}
                  </p>
                </div>
                <span className={`${styles.statusBadge} ${styles[STATUS_CLASS[currentYear.status] ?? 'statusDraft']}`}>
                  {t(STATUS_KEY[currentYear.status] ?? currentYear.status)}
                </span>
              </div>
              <div className={styles.progressTrack} role="progressbar" aria-valuenow={currentProgress} aria-valuemin={0} aria-valuemax={100}>
                <div className={styles.progressFill} style={{ width: `${currentProgress}%` }} />
              </div>
              <p className={styles.progressLabel}>
                {t('admin.academicYears.progressElapsed', { percent: currentProgress })}
              </p>
            </Card>
          ) : upcomingYear ? (
            <Card className={styles.currentCard}>
              <div className={styles.currentHeader}>
                <div>
                  <p className={styles.currentEyebrow}>{t('admin.academicYears.upcomingYear')}</p>
                  <h2 className={styles.currentLabel}>{upcomingYear.label}</h2>
                  <p className={styles.currentPeriod}>
                    {formatDate(upcomingYear.startDate)} – {formatDate(upcomingYear.endDate)} ·{' '}
                    {t('admin.academicYears.startsWhen', { when: formatRelative(upcomingYear.startDate, i18n.language) })}
                  </p>
                </div>
                <span className={`${styles.statusBadge} ${styles[STATUS_CLASS[upcomingYear.status] ?? 'statusDraft']}`}>
                  {t(STATUS_KEY[upcomingYear.status] ?? upcomingYear.status)}
                </span>
              </div>
            </Card>
          ) : null}

          <Card>
            <div className="table-scroll">
              <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('admin.academicYears.label')}</th>
                  <th>{t('admin.academicYears.period')}</th>
                  <th>{t('admin.academicYears.timing')}</th>
                  <th>{t('admin.academicYears.status')}</th>
                  <th>{t('admin.campuses.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedYears.map((year) => (
                  <tr key={year.id} className={year.id === currentYear?.id ? styles.currentRow : undefined}>
                    <td className={styles.labelCell}>
                      {year.label}
                      {year.id === currentYear?.id && (
                        <span className={styles.currentChip}>{t('admin.academicYears.current')}</span>
                      )}
                    </td>
                    <td>
                      <div>{formatDate(year.startDate)} – {formatDate(year.endDate)}</div>
                      <div className={styles.periodSub}>
                        {t('admin.academicYears.durationMonths', {
                          count: monthsBetween(year.startDate?.slice(0, 10) ?? '', year.endDate?.slice(0, 10) ?? ''),
                        })}
                      </div>
                    </td>
                    <td className={styles.timingCell}>{timingFor(year)}</td>
                    <td>
                      <span className={`${styles.statusBadge} ${styles[STATUS_CLASS[year.status] ?? 'statusDraft']}`}>
                        {t(STATUS_KEY[year.status] ?? year.status)}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actions}>
                        {year.status === 'SUBMITTED' && (
                          <Link to="/approvals" className={styles.awaitingLink}>
                            {t('admin.academicYears.awaitingApproval')}
                          </Link>
                        )}
                        {year.status === 'DRAFT' && (
                          <>
                            <Button size="sm" variant="secondary" onClick={() => openEdit(year)}>{t('admin.academicYears.edit')}</Button>
                            <Button size="sm" variant="secondary" onClick={() => setConfirmModal({ open: true, year, action: 'submit' })}>{t('admin.academicYears.submitForApproval')}</Button>
                            <Button size="sm" variant="danger" onClick={() => setConfirmModal({ open: true, year, action: 'delete' })}>{t('admin.academicYears.delete')}</Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      <Modal open={newModalOpen} onClose={() => setNewModalOpen(false)} title={t('admin.academicYears.newDialogTitle')} closeOnBackdropClick={false}>
        {yearForm('new', handleCreate)}
      </Modal>

      <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)} title={t('admin.academicYears.editDialogTitle')} closeOnBackdropClick={false}>
        {yearForm('edit', handleEdit)}
      </Modal>

      <Modal
        open={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, year: null, action: 'delete' })}
        title={confirmModal.action === 'delete' ? t('admin.academicYears.deleteConfirm') : t('admin.academicYears.submitConfirm')}
        description={confirmModal.year ? `${confirmModal.year.label} · ${formatDate(confirmModal.year.startDate)} – ${formatDate(confirmModal.year.endDate)}` : undefined}
      >
        <div className={styles.dialogActions}>
          <Button variant="secondary" onClick={() => setConfirmModal({ open: false, year: null, action: 'delete' })}>{t('common.cancel')}</Button>
          <Button
            variant={confirmModal.action === 'delete' ? 'danger' : 'primary'}
            onClick={confirmModal.action === 'delete' ? handleDelete : handleSubmitForApproval}
            loading={submitting}
            disabled={submitting}
          >
            {confirmModal.action === 'delete' ? t('admin.academicYears.delete') : t('admin.academicYears.submitForApproval')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
